import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  BookingRequestResponse,
  EventEditStatus,
  CyclingEvent,
  EventEdition,
  Organizer,
  OrganizerEditStatus,
  OrganizerInsights,
  OrganizerPortalEvent,
  OrganizerPortalSummary,
  OrganizerPortalSubmissions,
  eventWithOpenProposal,
} from '@radevents/shared';
import { useOrganizerPortal } from '../../contexts/OrganizerPortalContext';
import organizerPortalService from '../../services/organizerPortalService';
import organizerService from '../../services/organizerService';
import eventService from '../../services/eventService';
import EventFormModal, { EventFormValues, PreparedEventData, emptyEventForm } from '../EventFormModal';
import BookingRequestModal from '../BookingRequestModal';
import bookingService from '../../services/bookingService';
import { buildFormFromEvent } from '../../utils/eventForm';

import { billingDefaults } from './BillingFields';
import EventsTab from './manage/EventsTab';
import SubmissionsTab from './manage/SubmissionsTab';
import VisibilityTab from './manage/VisibilityTab';
import InsightsTab from './manage/InsightsTab';

type Tab = 'events' | 'einreichungen' | 'sichtbarkeit' | 'zahlen';

/**
 * „Sichtbarkeit" steht an zweiter Stelle, weil die Termine der Einstieg sind und das Angebot der
 * naechste Schritt. Der letzte Reiter ist die Nachschau — dort liegt alles, was der Veranstalter
 * abgeschickt hat und worauf wir antworten: Vorschlaege **und** Bestellungen. Der Zaehler zaehlt
 * beides, und seit dem 01.09.2026 sagt der Name das auch („Anfragen & Aenderungen" statt
 * „Eingereicht"): eine Bestellung reicht man nicht ein, man gibt sie auf.
 *
 * „Zahlen" heisst seit derselben Runde **Statistiken**. Das Wort ist eindeutiger — „Zahlen" stand
 * im selben Bereich auch fuer Preise und Betraege.
 *
 * Der zweite Reiter heisst seit dem 02.09.2026 wieder **Sichtbarkeit & Promotion** (davor kurz
 * „Premium & Werbung"). Das Gegenargument war, „Sichtbarkeit" beantworte im Briefkopf darueber
 * schon eine andere Frage — welche Stammdaten oeffentlich stehen. Es blieb eine Entscheidung fuer
 * das Wort, das der Bereich benutzt: der Reiter zeigt Logo, Titelbild, Bild und Flyer und die
 * Hervorhebung einzelner Events, also alles, was den Verein sichtbar macht. Der Schluessel bleibt
 * `sichtbarkeit` — er steht in keinem Link.
 */
const TABS: { key: Tab; label: string }[] = [
  { key: 'events', label: 'Meine Events' },
  { key: 'sichtbarkeit', label: 'Sichtbarkeit' },
  { key: 'zahlen', label: 'Statistiken' },
  { key: 'einreichungen', label: 'Bestellungen & Änderungen' },
];

interface Props {
  organizerId: number;
  /** Die Zusammenfassung aus dem Portal-Kontext — trägt u. a. `autoApproveEdits` und `premium`. */
  summary: OrganizerPortalSummary;
  /** Stammdaten, vom Elternteil geladen: der Briefkopf oben zeigt dieselben Werte. */
  profile: Organizer | null;
  /** Nach einer Änderung: der Elternteil lädt Profil und öffentliche Ansicht neu. */
  onProfileChanged: () => void;
  /**
   * Zähler: der Briefkopf oben hat einen Stammdaten-Vorschlag abgeschickt.
   *
   * Bearbeitet wird seit dem 26.08.2026 **im Briefkopf selbst** (`OrganizerLetterhead`), nicht
   * mehr in einem Dialog von hier aus. Geblieben ist die Nachwirkung: der neue Vorschlag muss im
   * Reiter *Eingereicht* auftauchen.
   */
  profileSavedSignal?: number;
  /** Es liegt eine Premium-Anfrage bei uns — steuert die Wortwahl der Karte in *Sichtbarkeit*. */
  premiumRequested?: boolean;
  /**
   * Welche Bilder gerade in der Prüfung liegen — die Seite weiß es, weil sie sie hochlädt.
   *
   * Der Reiter *Sichtbarkeit & Promotion* meldete sonst „Fehlt noch", während oben am Bild „Wird
   * geprüft" steht.
   */
  pendingAssets?: { logo?: boolean; banner?: boolean };
  /** Aus den Buchungen gelesen und nach oben gemeldet: der Pflege-Balken zeigt denselben Stand. */
  onPremiumRequestedChange?: (open: boolean) => void;
  /**
   * Öffnet den Premium-Dialog. Er wird von der Seite gehalten, nicht hier — alle Haken auf der
   * Veranstalterseite führen in denselben Dialog, und zwei Mount-Punkte hießen zwei Zustände
   * derselben Erklärung.
   */
  onPremiumHint?: () => void;
  /** Wie [onPremiumHint], aber ohne Verkaufsteil — der Aufrufer hat ihn schon gezeigt. */
  onPremiumOrder?: () => void;
  /** Einstieg von der öffentlichen Angebotsseite: öffnet den passenden Reiter ohne erneute Suche. */
  initialTab?: Tab;
  /** Stellt nach dem Einstieg „Event hervorheben" die Eventauswahl an den Anfang. */
  promotionIntent?: boolean;
}

/**
 * Der Pflege-Teil der Veranstalterseite: eigene Events, Einreichungen, Sichtbarkeit, Zahlen.
 *
 * Sitzt **auf** der öffentlichen Veranstalterseite und nicht daneben. Zwei Seiten für denselben
 * Verein hießen zwei Gestaltungen und zwei Wahrheiten — wer pflegt, soll sehen, was Besucher
 * sehen, und an derselben Stelle ändern können.
 *
 * Nichts davon geht direkt live. Jede Änderung ist ein Vorschlag und läuft durch dieselbe
 * Prüfung wie jeder andere — nur mit dem Vermerk, dass sie vom Veranstalter selbst kommt. Das
 * steht auch so auf der Seite: sonst wundert sich jemand, warum seine Korrektur nicht sofort
 * sichtbar ist, und reicht sie ein zweites Mal ein.
 *
 * Hier steht nur noch die Klammer: Laden, Zustand, die Reiterleiste und die beiden Dialoge. Was
 * ein Reiter zeigt, liegt in `manage/` — je Reiter eine Datei. Vorher waren es rund 950 Zeilen in
 * einer, und wer eine Zeile in der Terminliste ändern wollte, suchte länger als er baute.
 */
const OrganizerManage: React.FC<Props> = ({
  organizerId, summary, profile, onProfileChanged, profileSavedSignal,
  premiumRequested, onPremiumRequestedChange, onPremiumHint, onPremiumOrder, pendingAssets,
  initialTab,
  promotionIntent,
}) => {
  const { reload } = useOrganizerPortal();
  const selected = summary;
  const [tab, setTab] = useState<Tab>(initialTab ?? 'events');
  /**
   * Das Event, an das der Reiter *Sichtbarkeit & Promotion* geschickt hat.
   *
   * `EventsTab` scrollt hin und rahmt es kurz ein; danach meldet es sich zurueck und der Zustand
   * faellt weg. Er liegt hier und nicht im Reiter, weil ihn zwei Reiter gemeinsam haben — der
   * eine setzt ihn, der andere loest ihn ein.
   */
  const [focusEventId, setFocusEventId] = useState<number | null>(null);
  const clearFocus = useCallback(() => setFocusEventId(null), []);

  const [events, setEvents] = useState<OrganizerPortalEvent[]>([]);
  /**
   * Frühere Auflagen der eigenen Events — dieselbe Quelle wie auf der Veranstalterseite
   * (`OrganizerSeason`). Ohne sie stünde unter „Vergangene zeigen" je Event nur ein Termin,
   * weil ein Event vorgerückt und nicht neu angelegt wird.
   */
  const [editions, setEditions] = useState<EventEdition[]>([]);
  const [includePast, setIncludePast] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(true);

  const [submissions, setSubmissions] = useState<OrganizerPortalSubmissions | null>(null);

  const [insights, setInsights] = useState<OrganizerInsights | null>(null);
  /** Warum es keine Zahlen gibt — meistens: kein Premium. */
  const [insightsError, setInsightsError] = useState<string | null>(null);
  const [bookings, setBookings] = useState<BookingRequestResponse[]>([]);
  const [bookingFor, setBookingFor] = useState<
    { eventId?: number; eventTitle?: string; event?: CyclingEvent } | null
  >(null);
  /** Kurzform für die Haken: alle öffnen denselben Dialog, den die Seite hält. */
  const openPremium = () => onPremiumHint?.();
  const premium = !!summary.premium;

  const [allOrganizers, setAllOrganizers] = useState<Organizer[]>([]);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEventId, setEditingEventId] = useState<number | null>(null);
  /** Event, das nach dem Nachladen der Vergangenheit zum Bearbeiten geöffnet werden soll. */
  const [pendingEditEventId, setPendingEditEventId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<EventFormValues>(emptyEventForm);
  /** Feld, wegen dem das Formular offen ist (siehe EventFormProps.focusField). */
  const [formFocusField, setFormFocusField] = useState<'eventDate' | undefined>(undefined);
  const [savingEvent, setSavingEvent] = useState(false);
  /**
   * „Als Entwurf speichern" (27.09.2026). Ein Verein bereitet sein Event vor, ohne dass es sofort
   * im Kalender steht. Der Haken gehört zum Portal-Formular, nicht zum Redaktionsformular —
   * deshalb sitzt er hier im `extraFooter` und nicht in `EventForm`.
   *
   * Beim Bearbeiten eines Entwurfs ist er gesetzt; wer ihn dabei abwählt, veröffentlicht mit dem
   * Speichern. An einem veröffentlichten Event steht er nicht: ein Rückzug aus dem Kalender ist
   * eine andere Sache und geht über die Redaktion.
   */
  const [saveAsDraft, setSaveAsDraft] = useState(false);
  const [editingIsDraft, setEditingIsDraft] = useState(false);
  /** Das Formular setzt an einem offenen Vorschlag an statt am Event (der neue ersetzt ihn). */
  const [editingOpenProposal, setEditingOpenProposal] = useState(false);
  const [publishingId, setPublishingId] = useState<number | null>(null);

  const loadEvents = useCallback(() => {
    if (!organizerId) return;
    setLoadingEvents(true);
    organizerPortalService.events(organizerId, includePast)
      .then(setEvents)
      .catch(() => toast.error('Events konnten nicht geladen werden'))
      .finally(() => setLoadingEvents(false));
  }, [organizerId, includePast]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  // Erst beim Aufklappen: wer nur seine kommenden Termine ansieht, braucht die Vergangenheit
  // nicht, und die Liste wird auf jeder Seite des Bereichs geladen.
  useEffect(() => {
    if (!organizerId || !includePast) return;
    eventService.getOrganizerEditions(organizerId).then(setEditions).catch(() => setEditions([]));
  }, [organizerId, includePast]);

  useEffect(() => {
    if (profileSavedSignal && organizerId) {
      organizerPortalService.submissions(organizerId).then(setSubmissions).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileSavedSignal]);

  const loadBookings = useCallback(() => {
    if (!organizerId) return;
    bookingService.myBookings(organizerId)
      .then(all => {
        setBookings(all);
        // Der Pflege-Balken über der Seite zeigt denselben Stand („Premium angefragt" statt
        // „Basis-Zugang"). Er gehört der Seite, die Buchungen liegen hier — also gemeldet statt
        // ein zweites Mal geladen.
        const open = all.find(b => b.kind === 'VERIFICATION' && b.status === 'OPEN');
        onPremiumRequestedChange?.(!!open);
      })
      .catch(() => setBookings([]));
  }, [organizerId, onPremiumRequestedChange]);

  useEffect(() => {
    if (!organizerId) return;
    organizerPortalService.submissions(organizerId).then(setSubmissions).catch(() => setSubmissions(null));
    loadBookings();
    organizerPortalService.insights(organizerId)
      .then(r => { setInsights(r); setInsightsError(null); })
      .catch(err => {
        setInsights(null);
        setInsightsError(err.response?.data?.error
          ?? 'Die Zahlen konnten nicht geladen werden.');
      });
  }, [organizerId, loadBookings]);

  // Das Event-Formular erwartet die Veranstalter-Liste für sein Auswahlfeld. Im Portal steht der
  // Veranstalter ohnehin fest (er kommt serverseitig aus dem Pfad) — die Liste ist nur Anzeige.
  useEffect(() => {
    organizerService.listPublic().then(setAllOrganizers).catch(() => setAllOrganizers([]));
  }, []);

  const openCreate = () => {
    setEditingEventId(null);
    setEditingOpenProposal(false);
    setEditingIsDraft(false);
    setSaveAsDraft(false);
    setFormFocusField(undefined);
    setFormValues({ ...emptyEventForm, organizerId: organizerId ?? null });
    setShowEventForm(true);
  };

  const openEdit = async (entry: OrganizerPortalEvent, intent?: 'next-edition') => {
    // Läuft schon eine Änderung, geht es dort weiter: der neue Vorschlag ersetzt den offenen und
    // muss deshalb alles tragen, was der schon geändert hat (EventEditService).
    const open = entry.pendingEditId
      ? await eventService.getMyOpenEdit(entry.event.id).catch(() => null)
      : null;
    setEditingOpenProposal(!!open);
    setEditingEventId(entry.event.id);
    const isDraft = entry.event.status === 'DRAFT';
    setEditingIsDraft(isDraft);
    setSaveAsDraft(isDraft);
    // Kommt der Klick von „Neuen Termin eintragen“, ist das Formular wegen des Datums offen.
    setFormFocusField(intent === 'next-edition' ? 'eventDate' : undefined);
    const base = {
      ...entry.event,
      registrationStart: entry.registrationStart || undefined,
      registrationMode: entry.registrationMode,
      registrationState: entry.registrationState,
    };
    const values = buildFormFromEvent(open ? eventWithOpenProposal(base, open) : base);
    setFormValues({ ...values, registrationStart: values.registrationStart || '' });
    setShowEventForm(true);
  };

  /**
   * Aus „Event eintragen" in die Bearbeitung des vorhandenen Events wechseln — der Weg, auf dem
   * aus einem zweiten Event die nächste Auflage wird (siehe EventFormModal, `onUseAsEdition`).
   *
   * Steht das Event nicht in der Liste, liegt es weiter zurück und die Liste zeigt gerade keine
   * Vergangenheit: dann einschalten und öffnen, sobald nachgeladen ist. Gehört es einem anderen
   * Veranstalter, kommt es nie an — das sagen wir, statt stumm zu bleiben.
   */
  const useAsEdition = (event: CyclingEvent) => {
    const entry = events.find(e => e.event.id === event.id);
    if (entry) { openEdit(entry, 'next-edition'); return; }
    setIncludePast(true);
    setPendingEditEventId(event.id);
  };

  useEffect(() => {
    if (pendingEditEventId == null || loadingEvents) return;
    const entry = events.find(e => e.event.id === pendingEditEventId);
    setPendingEditEventId(null);
    if (entry) openEdit(entry, 'next-edition');
    else toast.error('Dieses Event gehört zu einem anderen Veranstalter. Schreib uns, dann ordnen wir es zu.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, loadingEvents, pendingEditEventId]);

  const handleSaveEvent = async (data: PreparedEventData) => {
    if (!organizerId) return;
    setSavingEvent(true);
    try {
      const gpxFiles = data.gpxFiles ?? [];
      const payload = {
        ...data,
        imagePending: !!data.imageFile,
        flyerPending: !!data.flyerFile,
        gpxPending: gpxFiles.length > 0,
        saveAsDraft,
      };
      const created = editingEventId
        ? await organizerPortalService.proposeEventEdit(organizerId, editingEventId, payload)
        : await organizerPortalService.proposeNewEvent(organizerId, payload);

      // Foto und Strecken hängen an der Vorschlags-Id — die gibt es erst jetzt. Scheitert ein
      // Upload, ist der Vorschlag selbst trotzdem eingereicht; das sagen wir auch so.
      let uploadFailed = false;
      if (data.imageFile) {
        try {
          await eventService.uploadProposalImage(created.id, data.imageFile);
        } catch { uploadFailed = true; }
      }
      if (data.flyerFile) {
        try {
          await eventService.uploadProposalFlyer(created.id, data.flyerFile);
        } catch { uploadFailed = true; }
      }
      for (const gpx of gpxFiles) {
        const distance = created.distances?.find(d => d.distanceKm === gpx.distanceKm);
        if (!distance?.id) { uploadFailed = true; continue; }
        try {
          await eventService.uploadProposalGpx(created.id, distance.id, gpx.file);
        } catch { uploadFailed = true; }
      }

      // Mit Anhängen gibt der Server erst frei, wenn wir melden, dass alle Dateien oben sind.
      const hadAttachments = !!data.imageFile || !!data.flyerFile || gpxFiles.length > 0;
      const final = hadAttachments
        ? await eventService.finishProposalAttachments(created.id).catch(() => created)
        : created;

      // Was hier steht, muss stimmen: bei einem Veranstalter mit automatischer Freigabe ist die
      // Änderung sofort online, sonst wartet sie auf die Redaktion. Der Server sagt uns, was gilt.
      const live = final.status === 'APPROVED';
      // Ein Entwurf ist nie „online" — auch nicht bei Auto-Freigabe. Die Rückmeldung sagt, wo er
      // liegt und was der nächste Schritt ist; sonst sucht ihn der Verein im Kalender.
      toast.success(uploadFailed
        ? 'Gespeichert — eine Datei konnte nicht übertragen werden'
        : saveAsDraft
          ? (live
            ? 'Als Entwurf gespeichert. Noch nicht öffentlich — veröffentlichen kannst du es unter „Meine Events“.'
            : 'Als Entwurf gespeichert. Nach kurzer Prüfung steht es unter „Meine Events“, noch nicht öffentlich.')
          : live
            ? 'Gespeichert. Die Änderung ist online.'
            : 'Gespeichert. Sie erscheint auf der Seite, sobald wir sie kurz geprüft haben.');
      setShowEventForm(false);
      loadEvents();
      reload();
      if (organizerId) organizerPortalService.submissions(organizerId).then(setSubmissions).catch(() => {});
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Vorschlag konnte nicht eingereicht werden');
    } finally {
      setSavingEvent(false);
    }
  };

  /**
   * „Veröffentlichen" an einem Entwurf. Der Server legt einen Vorschlag an: bei Auto-Freigabe ist
   * das Event sofort online, sonst prüft die Redaktion noch einmal — sie hat beim Anlegen einen
   * Entwurf gesehen, nicht das, was seitdem daran geändert wurde.
   */
  const handlePublish = async (entry: OrganizerPortalEvent) => {
    if (!organizerId) return;
    setPublishingId(entry.event.id);
    try {
      const created = await organizerPortalService.publishEvent(organizerId, entry.event.id);
      toast.success(created.status === 'APPROVED'
        ? 'Veröffentlicht. Das Event steht jetzt im Kalender.'
        : 'Zur Veröffentlichung eingereicht. Es erscheint, sobald wir es kurz geprüft haben.');
      loadEvents();
      reload();
      organizerPortalService.submissions(organizerId).then(setSubmissions).catch(() => {});
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Das Event konnte nicht veröffentlicht werden');
    } finally {
      setPublishingId(null);
    }
  };

  /** Was gerade auf uns wartet — Vorschläge **und** Bestellungen, sie stehen im selben Reiter. */
  const openSubmissionCount = useMemo(
    () => (submissions?.eventEdits.filter(e => e.status === EventEditStatus.PENDING).length ?? 0)
      + (submissions?.organizerEdits.filter(e => e.status === OrganizerEditStatus.PENDING).length ?? 0)
      + bookings.filter(b => b.status === 'OPEN').length,
    [submissions, bookings],
  );

  if (!selected) return null;

  /**
   * Pfeiltasten wechseln den Reiter, Pos1/Ende springen an die Enden.
   *
   * Ohne das ist eine Reiterleiste mit `tabIndex={-1}` an den inaktiven Knöpfen für
   * Tastaturnutzer eine Sackgasse — sie erreichen die anderen Bereiche gar nicht mehr.
   * Der Fokus wandert mit, sonst zeigt die Auswahl woanders hin als der Rahmen.
   */
  const onTabKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    e.preventDefault();
    const i = TABS.findIndex(t => t.key === tab);
    const next = e.key === 'Home' ? 0
      : e.key === 'End' ? TABS.length - 1
      : e.key === 'ArrowLeft' ? (i - 1 + TABS.length) % TABS.length
      : (i + 1) % TABS.length;
    setTab(TABS[next].key);
    document.getElementById(`vb-tab-${TABS[next].key}`)?.focus();
  };

  /** Hülle je Reiter: verbindet Panel und Knopf, damit ein Vorlesegerät beides zusammen meldet. */
  const panel = (key: Tab, content: React.ReactNode) => (
    <div role="tabpanel" id={`vb-panel-${key}`} aria-labelledby={`vb-tab-${key}`}>
      {content}
    </div>
  );

  return (
    <>
      <nav className="vb-tabs" role="tablist" aria-label="Bereiche">
        {TABS.map(t => (
          <button
            key={t.key}
            id={`vb-tab-${t.key}`}
            role="tab"
            aria-selected={tab === t.key}
            aria-controls={`vb-panel-${t.key}`}
            /* Nur der aktive Reiter liegt im Tab-Lauf; zwischen den Reitern führen die
               Pfeiltasten. So ist die Reiterleiste eine Station und nicht vier. */
            tabIndex={tab === t.key ? 0 : -1}
            className={`vb-tab${tab === t.key ? ' vb-tab--on' : ''}`}
            onClick={() => setTab(t.key)}
            onKeyDown={onTabKeyDown}
            aria-label={t.key === 'einreichungen' && openSubmissionCount > 0
              ? `${t.label}, ${openSubmissionCount} offen`
              : undefined}
          >
            {t.label}
            {t.key === 'einreichungen' && openSubmissionCount > 0 && (
              <span className="vb-tab-count" aria-hidden>{openSubmissionCount}</span>
            )}
          </button>
        ))}
      </nav>

      {tab === 'events' && panel('events',
        <EventsTab
          events={events}
          loading={loadingEvents}
          includePast={includePast}
          onIncludePastChange={setIncludePast}
          premium={premium}
          bookings={bookings}
          editions={editions}
          onCreate={openCreate}
          onEdit={openEdit}
          onPromote={entry => setBookingFor({
            eventId: entry.event.id, eventTitle: entry.event.title,
            // Der ganze Datensatz, damit der Dialog die Stufen an DIESER Card zeigen kann
            // statt an einer Skizze.
            event: entry.event,
          })}
          onPublish={entry => { if (publishingId === null) handlePublish(entry); }}
          onPremiumHint={openPremium}
          premiumRequested={premiumRequested}
          focusEventId={focusEventId}
          onFocusHandled={clearFocus}
        />,
      )}

      {tab === 'einreichungen' && organizerId && panel('einreichungen',
        <SubmissionsTab
          organizerId={organizerId}
          bookings={bookings}
          submissions={submissions}
          onBookingsChanged={loadBookings}
        />,
      )}

      {tab === 'sichtbarkeit' && panel('sichtbarkeit',
        <VisibilityTab
          organizerId={organizerId}
          premium={premium}
          verification={profile?.verification}
          requested={premiumRequested}
          profile={profile}
          pendingAssets={pendingAssets}
          events={events}
          bookings={bookings}
          onPremiumHint={openPremium}
          onOrder={() => onPremiumOrder?.()}
          /* Derselbe Weg wie am Termin: **ein** Dialog, zweimal geoeffnet. Ihn hier wegzulassen
             hiess, dass im Abschnitt „Events hervorheben" nichts hervorzuheben ging. */
          onPromote={entry => setBookingFor({
            eventId: entry.event.id, eventTitle: entry.event.title, event: entry.event,
          })}
          /* Der stille zweite Knopf: fuehrt an genau diesen Termin, nicht nur in den Reiter. Wer
             die Buehne buchen will, braucht dort ein Foto. */
          onGoToEvents={eventId => { setFocusEventId(eventId); setTab('events'); }}
          promotionIntent={promotionIntent}
        />,
      )}

      {tab === 'zahlen' && panel('zahlen',
        <InsightsTab organizerId={organizerId} events={events} insights={insights}
          insightsError={insightsError} onPremiumHint={openPremium}
          premiumRequested={premiumRequested} />,
      )}

      {organizerId && (
        <BookingRequestModal
          show={bookingFor !== null}
          organizerId={organizerId}
          billingDefault={billingDefaults(profile, selected.name)}
          eventId={bookingFor?.eventId}
          eventTitle={bookingFor?.eventTitle}
          event={bookingFor?.event}
          onClose={() => setBookingFor(null)}
          onBooked={loadBookings}
        />
      )}

      <EventFormModal
        focusField={formFocusField}
        onUseAsEdition={editingEventId ? undefined : useAsEdition}
        show={showEventForm}
        modalTitle={editingEventId ? (editingOpenProposal ? 'Änderung weiter bearbeiten' : 'Event bearbeiten') : 'Event eintragen'}
        saveLabel="Speichern"
        initialValues={formValues}
        editingId={editingEventId}
        organizers={allOrganizers}
        onOrganizersRefresh={() => {}}
        saving={savingEvent}
        onSave={handleSaveEvent}
        onClose={() => setShowEventForm(false)}
        extraFooter={(!editingEventId || editingIsDraft) ? (
          <label className="vb-draft-toggle">
            <input
              type="checkbox"
              checked={saveAsDraft}
              onChange={e => setSaveAsDraft(e.target.checked)}
            />
            <span>
              <strong>Als Entwurf speichern</strong>
              <span className="vb-draft-toggle-hint">
                {editingIsDraft && !saveAsDraft
                  ? 'Mit dem Speichern wird das Event veröffentlicht.'
                  : 'Das Event steht dann nur unter „Meine Events“, nicht im Kalender. Veröffentlichen kannst du es dort jederzeit.'}
              </span>
            </span>
          </label>
        ) : undefined}
      />


    </>
  );
};

export default OrganizerManage;
